import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { SYMPTOM_MAX_LENGTH } from '../../../types/diagnostics';
import { normaliseSymptom } from './reportLogic';

/**
 * "What did the skipper report?" — the one question Claude most needs
 * answered, asked inline so it works one-handed on a phone. Optional: an empty
 * box sends no symptom.
 */
export function SymptomForm({
  initialValue = '',
  helper,
  submitLabel,
  pendingLabel,
  pending = false,
  onSubmit,
  onCancel,
}: {
  initialValue?: string;
  helper?: ReactNode;
  submitLabel: string;
  pendingLabel: string;
  pending?: boolean;
  onSubmit: (symptom: string | undefined) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [value, setValue] = useState(initialValue);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(normaliseSymptom(value));
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <label htmlFor={id} className="block text-[15px] font-medium text-slate-200">
        What did the skipper report? <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={SYMPTOM_MAX_LENGTH}
        rows={3}
        placeholder="e.g. Since last night's update the chartplotter is stuck on the NMEA screen"
        className="block w-full rounded-lg border border-slate-600 bg-slate-900/60 px-3 py-2 text-[16px] text-slate-100 placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
      />
      <div className="flex flex-wrap items-start justify-between gap-2">
        {helper ? <p className="min-w-0 flex-1 text-[14px] text-slate-400">{helper}</p> : <span />}
        <span className="text-[13px] text-slate-500">{value.length}/{SYMPTOM_MAX_LENGTH}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending ? pendingLabel : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="rounded-lg bg-slate-700 px-4 py-3 text-[15px] text-slate-200 transition-colors hover:bg-slate-600 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default SymptomForm;
